import classNames from 'classnames';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import { get } from 'lodash';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { ShopBadges } from '@reverbdotcom/commons/src/components/shop_badges';

import DEFAULT from '../../../../images/default-avatar.jpg';
import { CoreShopContainer } from '../../../schemas/rql';
import FreeShippingCallout from './free_shipping_callout';
import URLHelpers, { adminEditUserPath, shopPromotePath } from '../../shared/url_helpers';
import FavoriteShopCTA from '../../../../src/components/favorites/favorite_shop_cta';

import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useTheme } from '@reverbdotcom/commons/src/components/theme_context_provider';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { RCIcon, buttonClassName } from '@reverbdotcom/cadence/components';
import { CommentOIcon, MagicIcon } from '@reverbdotcom/cadence/icons/react';

function OwnerLinks() {
  return (
    <ul className="mt-half">
      <li className="d-inline-block mtb-half mlr-half">
        <CoreLink
          to={URLHelpers.shopEditPath()}
          className="button button--small"
        >
          {I18n.t('discovery.shopHeader.editShop')}
        </CoreLink>
      </li>
      <li className="d-inline-block mtb-half mlr-half">
        <CoreLink
          to={shopPromotePath}
          className="button button--small"
        >
          {I18n.t('discovery.shopHeader.promoteShop')}
        </CoreLink>
      </li>
    </ul>
  );
}

function Placeholder() {
  const classes = classNames(
    'shop-headerv2',
    'shop-headerv2--placeholder',
    'shop-headerv2--with-banner',
  );

  return (
    <div className={classes}>
      <div className="shop-headerv2__banner" />
      <div className="shop-headerv2__main">
        <div className="shop-headerv2__main__avatar" />
        <div className="shop-headerv2__main__text" />
      </div>
    </div>
  );
}

function BadgeContainer({ shop, stacked }: { shop: CoreShopContainer.Shop, stacked: boolean }) {
  return (
    <div className="shop-headerv2__main__badges">
      <ShopBadges
        preferredSeller={shop.preferredSeller}
        quickResponder={shop.quickResponder}
        quickShipper={shop.quickShipper}
        stacked={stacked}
      />
    </div>
  );
}

interface IProps {
  shop: CoreShopContainer.Shop;
  initialFavoriteId: string | null;
}

export const ShopHeaderFragment = gql`
  fragment ShopHeaderFragment on Shop {
    _id
    id
    slug
    name
    preferredSeller
    quickShipper
    quickResponder
    description
    userUuid
    userId
    address {
      displayLocation
    }
    banner {
      source
    }
    user {
      _id
      id
      avatar {
        source
      }
    }
  }
`;

export function ShopHeader({
  shop,
  initialFavoriteId,
}: IProps) {
  const { theme } = useTheme();
  const user = useUser();

  const webTwoTheme = theme === ThemeType.web2;
  const shouldRenderFreeShippingCallout = !!shop?.freeDomesticShippingRate && shop?.freeDomesticShippingRate?.regionCode === user.shippingRegionCode;
  const shouldRenderOwnerLinks = `${user.id}` === shop?.userId;
  const avatarUrl = get(shop, 'user.avatar.source', false);
  const bannerUrl = get(shop, 'banner.source');

  if (!shop) return (<Placeholder/>);

  const classes = classNames(
    'shop-headerv2',
    { 'shop-headerv2--with-banner': !!bannerUrl },
  );

  return (
    <div className={classes}>
      {bannerUrl && (
        <div className="shop-headerv2__banner">
          {webTwoTheme && (
            <>
              <img className="shop-headerv2__banner__bg" src={bannerUrl} alt={shop.name} />
              <div className="shop-headerv2__banner__blur" />
            </>
          )}
          <img className="shop-headerv2__banner__main" src={bannerUrl} alt={shop.name} />
        </div>
      )}
      <div className="shop-headerv2__main">
        <div className="shop-headerv2__main__avatar">
          <img src={avatarUrl || DEFAULT} alt={shop.name} />
        </div>
        <div className="shop-headerv2__main__text">
          <h1 className="shop-headerv2__title">
            {shop.name}
            <FavoriteShopCTA
              shop={shop}
              initialFavoriteId={initialFavoriteId}
              parentComponentName="ShopHeader"
            />
            {user.isAdmin && (
              <a
                href={adminEditUserPath.expand({ id: shop.userId })}
                data-edit-link
                className="shop-headerv2__admin-link"
              >
                <RCIcon svgComponent={() => <MagicIcon />} inline />
              </a>
            )}
          </h1>
          <ul className="shop-headerv2__details">
            <li>
              <span className="icon-l-map-marker">
                {shop.address.displayLocation}
              </span>
            </li>
            <li>
              <a
                href={URLHelpers.newDashboardMessagesPath(null, shop.userUuid)}
                className={webTwoTheme ?
                  buttonClassName({ variant: 'muted', theme: 'main', size: 'mini' }) :
                  'icon-l-comment-o'
                }
              >
                {webTwoTheme && (
                  <span className="mr-1">
                    <RCIcon svgComponent={() => <CommentOIcon />} inline />
                  </span>
                )}
                {I18n.t('discovery.shopHeader.message')}
              </a>
            </li>
          </ul>
          <div className="shop-headerv2__bio">
            <SanitizedRender
              html={shop.description}
              truncationLimit={200}
              htmlTag="div"
              convertNewlines
            />
          </div>
          <>
            {shouldRenderFreeShippingCallout && <FreeShippingCallout shop={shop} />}
            {shouldRenderOwnerLinks && <OwnerLinks />}
          </>
          {webTwoTheme && <BadgeContainer shop={shop} stacked={false} />}
        </div>
        {!webTwoTheme && <BadgeContainer shop={shop} stacked />}
      </div>
    </div>
  );
}

export default ShopHeader;
